/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

import { registerField } from 'core/forms';
import { addToast, handleAxiosError } from 'core/notifications';

import { Button } from 'uikit/buttons';
import { Loading } from 'uikit/loaders';

import FileListItem from '@/exceed/components/fileListItem';
import useGlobalQueries from '@/exceed/hooks/useGlobalQueries';
import useGoogleDrivePicker from '@/exceed/hooks/useGoogleDrivePicker';
import useIsFeatureEnabled from '@/exceed/hooks/useIsFeatureEnabled';
import usePreviousState from '@/exceed/hooks/usePreviousState';
import { fileUploader } from '@/files';

import getAdminCloudSettings from '../helpers/getAdminCloudSettings';
import getFilesToRender from '../helpers/getFilesToRender';
import handleSourceMaterialMaxFileSizeError from '../helpers/handleSourceMaterialMaxFileSizeError';
import trackSourceMaterialFileUploadSize from '../helpers/trackSourceMaterialFileUploadSize';

import styles from '../styles/formFieldGoogleDrive.module.scss';

const LoadingOverlay = () => (
  <div className={styles.loadingOverlay}>
    <Loading />
  </div>
);

const downloadFile = async ({ accessToken, id, name, mimeType }) => {
  if (mimeType.startsWith('application/vnd.google-apps')) {
    const url = `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=application/pdf`;
    const file = await axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: 'blob',
      })
      .then((response) => new File([response.data], `${name}.pdf`, { type: 'application/pdf' }));

    return file;
  }

  const url = `https://www.googleapis.com/drive/v3/files/${id}?alt=media`;
  const file = await axios
    .get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then((response) => new File([response.data], name, { type: mimeType }));

  return file;
};

const FormFieldGoogleDrive = ({
  fieldKey,
  fieldId,
  schema: {
    maxUploadSizeInMb = 500,
    mimeTypes = [],
    setIsFileUploading,
    showUploadView,
    showUploadFolders,
    supportDrives,
    multiselect,
  },
  updateField,
  value = [],
}) => {
  const [uploadMap, setUploadMap] = useState({});
  const prevUploadMap = usePreviousState(uploadMap);
  const files = useMemo(() => getFilesToRender({ uploadMap, value }), [uploadMap, value]);
  const fieldRef = useRef();
  const dialogRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { adminSettings, currentUser } = useGlobalQueries();
  const { locale } = currentUser.data;
  const cloudSettings = getAdminCloudSettings(adminSettings);
  const google_client_id = cloudSettings.file_picker_settings?.google_drive?.client_id;
  const google_api_developer_key =
    cloudSettings.file_picker_settings?.google_drive?.api_developer_key;
  const google_app_id = google_client_id?.split('-')?.[0] || '';
  const hasGoogleDriveFeature = useIsFeatureEnabled('enable_google_drive_file_picker_integration');
  const googleDriveEnabled = hasGoogleDriveFeature && google_client_id && google_api_developer_key;

  useEffect(() => {
    const completedUploads = Object.values(uploadMap).filter(({ status }) => status === 'complete');

    if (completedUploads.length > 0) {
      const newFiles = completedUploads.map(({ file }) => file);

      setUploadMap((map) => {
        const newUploadMap = { ...map };

        completedUploads.forEach(({ file }) => {
          delete newUploadMap[file.name];
        });

        return newUploadMap;
      });

      updateField(sortBy([...value, ...newFiles], 'name'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateField, uploadMap]);

  useEffect(() => {
    if ((!prevUploadMap || !Object.keys(prevUploadMap).length) && Object.keys(uploadMap).length) {
      setIsFileUploading(true);
    } else if (
      prevUploadMap &&
      Object.keys(prevUploadMap).length &&
      !Object.keys(uploadMap).length
    ) {
      setIsFileUploading(false);
    }
  }, [prevUploadMap, setIsFileUploading, uploadMap]);

  const hideDialog = () => {
    if (!dialogRef.current) {
      dialogRef.current = fieldRef.current.closest('[role="dialog"]');
    }

    dialogRef.current.style.display = 'none';
  };

  const unsetDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.style.display = 'unset';
    }
  };

  const pickerCallback = async (payload, accessToken) => {
    if (payload.action === 'cancel' || payload.action === 'error') {
      unsetDialog();
    }

    if (payload.action === 'loaded') {
      const pickerBgElement = document.querySelector(':not(iframe).picker.picker-dialog-bg');

      if (pickerBgElement) {
        pickerBgElement.style.opacity = '1';
      }
    }

    if (payload.action === 'picked') {
      if (Array.isArray(payload.docs) && payload.docs.length > 0) {
        setIsLoading(true);
        unsetDialog();

        try {
          const downloadRequests = payload.docs.map(async ({ id, name, mimeType, ...metadata }) => {
            const file = await downloadFile({ accessToken, id, name, mimeType });

            if (
              files.some(
                ({ name: fileName, resource }) =>
                  resource?.external_id === id || fileName === file.name,
              )
            ) {
              addToast(i18n.t('file-already-exists'), 'error');
              return;
            }

            const maxFileSize =
              typeof maxUploadSizeInMb === 'number'
                ? maxUploadSizeInMb
                : maxUploadSizeInMb(mimeType);

            if (file.size > maxFileSize * 1000000) {
              handleSourceMaterialMaxFileSizeError({ maxFileSize, mimeType });
              trackSourceMaterialFileUploadSize({
                fileSize: file.size,
                uploadStatus: 'failure',
              });
              return;
            }

            return {
              file,
              data: {
                material_type: 'Resource',
                import_type: fieldKey,
                file_size: file.size,
                name: file.name,
                resource: {
                  external_id: id,
                  mime: file.type,
                  metadata,
                },
                status: 'created',
              },
            };
          });

          await Promise.all(downloadRequests)
            .then(async (downloadedFiles) => {
              setIsLoading(false);

              const uploadFileRequests = downloadedFiles
                .filter((file) => file)
                .map(({ file, data }) =>
                  fileUploader(file, {
                    start: () => {
                      setUploadMap((map) => ({
                        ...map,
                        [data.name]: {
                          status: 'uploading',
                          progress: 0,
                          file: data,
                        },
                      }));
                    },
                    progress: (percent) => {
                      if (percent !== 100) {
                        setUploadMap((map) => ({
                          ...map,
                          [data.name]: {
                            ...map[data.name],
                            progress: percent,
                          },
                        }));
                      }
                    },
                    complete: (completionObj) => {
                      const fileUpload = { ...data };
                      fileUpload.resource.url = completionObj.url || completionObj;

                      trackSourceMaterialFileUploadSize({
                        fileSize: file.size,
                        uploadStatus: 'success',
                      });
                      setUploadMap((map) => ({
                        ...map,
                        [data.name]: {
                          status: 'complete',
                          progress: 100,
                          file: fileUpload,
                        },
                      }));
                    },
                    error: (errorMessage) => {
                      setUploadMap((map) => {
                        const newMap = { ...map };

                        delete newMap[data.name];

                        return newMap;
                      });

                      addToast(errorMessage, 'error');
                    },
                  }),
                );

              await Promise.all(uploadFileRequests).catch((error) => {
                throw error;
              });
            })
            .catch((error) => {
              throw error;
            });
        } catch (error) {
          setIsLoading(false);
          handleAxiosError(error);
        }
      }
    }
  };

  const openPicker = useGoogleDrivePicker({
    clientId: google_client_id,
    developerKey: google_api_developer_key,
    appId: google_app_id,
    onError: unsetDialog,
    onSuccess: hideDialog,
    locale,
    viewId: 'DOCS',
    showUploadView,
    showUploadFolders,
    supportDrives,
    mimeTypes,
    multiselect,
    pickerCallback,
  });

  const onClick = () => {
    openPicker();
  };

  const onRemove = (removeFile) => {
    const filteredValue = value.filter((file) => !isEqual(file, removeFile));
    updateField(filteredValue);
  };

  if (!googleDriveEnabled) {
    return (
      <div id={fieldId}>
        <p
          className={styles.disabledMessage}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: i18n.t('file-picker-not-enabled') }}
        />
      </div>
    );
  }

  return (
    <div id={fieldId} ref={fieldRef}>
      <ul className={styles.list}>
        {files.map((item, index) => {
          const { file_size, material_type, name } = item;
          const upload = uploadMap[name] || null;

          return (
            <FileListItem
              // eslint-disable-next-line react/no-array-index-key
              key={`source_material_google_drive_${index}`}
              name={name}
              fileSize={file_size}
              materialType={material_type}
              onRemove={() => onRemove(item)}
              upload={upload}
            />
          );
        })}
      </ul>
      <Button variant="outlined" onClick={onClick}>
        {i18n.t('choose-file')}
      </Button>
      {isLoading && <LoadingOverlay />}
    </div>
  );
};

FormFieldGoogleDrive.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    maxUploadSizeInMb: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
    mimeTypes: PropTypes.arrayOf(PropTypes.string),
    multiselect: PropTypes.bool,
    setIsFileUploading: PropTypes.func.isRequired,
    showUploadView: PropTypes.bool,
    showUploadFolders: PropTypes.bool,
    supportDrives: PropTypes.bool,
  }).isRequired,
  updateField: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      material_type: PropTypes.oneOf(['Course', 'Resource']).isRequired,
      import_type: PropTypes.oneOf(['file_upload', 'google_drive', 'one_drive']),
      file_size: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      resource: PropTypes.shape({
        external_id: PropTypes.string,
        mime: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        metadata: PropTypes.shape({}),
      }),
      status: PropTypes.oneOf([
        'created',
        'failure',
        'rag_token_requested',
        'request_file_transcript',
        'requested',
        'success',
      ]).isRequired,
    }),
  ),
};

registerField('GoogleDrivePicker', FormFieldGoogleDrive);

export default FormFieldGoogleDrive;
